import React, { useState } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';

const RecebimentosTable = ({ 
  data = [], 
  columns = [], 
  loading = false,
  maxHeight = 'max-h-[28rem]',
  emptyMessage = 'Nenhum dado encontrado'
}) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedData = React.useMemo(() => {
    if (!sortConfig.key) return data;
    
    return [...data].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      
      // Tratar valores numéricos
      const aNum = typeof aValue === 'number' ? aValue : parseFloat(aValue) || 0;
      const bNum = typeof bValue === 'number' ? bValue : parseFloat(bValue) || 0;
      
      if (aNum < bNum) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aNum > bNum) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      
      // Fallback para strings
      const aStr = String(aValue || '').toLowerCase();
      const bStr = String(bValue || '').toLowerCase();
      
      if (aStr < bStr) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aStr > bStr) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [data, sortConfig]);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[color:var(--accent)]"></div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className={`${maxHeight} overflow-y-auto overflow-x-auto`}>
        <table className="w-full divide-y divide-[color:var(--border)]">
          <thead className="bg-[color:var(--surface-muted)] sticky top-0 z-10">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={`
                    px-3 py-2 text-xs sm:text-sm font-medium text-[color:var(--text-muted)] uppercase tracking-wider
                    ${column.cellClassName?.includes('text-right') ? 'text-right' : 'text-left'}
                    ${column.cellClassName || ''}
                  `}
                  onClick={() => column.sortable && handleSort(column.key)}
                  style={{ 
                    cursor: column.sortable ? 'pointer' : 'default'
                  }}
                >
                  <div className={`flex items-center gap-1 ${column.cellClassName?.includes('text-right') ? 'justify-end' : ''}`}>
                    <span className="truncate">{column.label}</span>
                    {column.sortable && (
                      <div className="flex flex-col flex-shrink-0">
                        <ChevronUp 
                          className={`w-3 h-3 ${
                            sortConfig.key === column.key && sortConfig.direction === 'asc' 
                              ? 'text-[color:var(--accent)]' 
                              : 'text-[color:var(--text-muted)]'
                          }`} 
                        />
                        <ChevronDown 
                          className={`w-3 h-3 -mt-1 ${
                            sortConfig.key === column.key && sortConfig.direction === 'desc' 
                              ? 'text-[color:var(--accent)]' 
                              : 'text-[color:var(--text-muted)]'
                          }`} 
                        />
                      </div>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-[color:var(--surface)] divide-y divide-[color:var(--border)]">
            {sortedData.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-6 py-12 text-center text-[color:var(--text-muted)] text-sm">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              sortedData.map((row, index) => (
                <tr 
                  key={index} 
                  className="hover:bg-[color:var(--surface-muted)] transition-colors"
                >
                  {columns.map((column) => {
                    const cellValue = column.render 
                      ? column.render(row[column.key], row, index) 
                      : row[column.key];
                    
                    return (
                      <td 
                        key={column.key} 
                        className={`
                          px-3 py-2 text-xs sm:text-sm
                          ${column.cellClassName || ''}
                        `}
                      >
                        <div className={column.cellClassName?.includes('break-words') 
                          ? 'break-words whitespace-normal' 
                          : 'truncate'
                        }>
                          {cellValue}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      
      {data.length > 0 && (
        <div className="px-3 sm:px-6 py-2 bg-[color:var(--surface-muted)] border-t border-[color:var(--border)]">
          <div className="flex items-center justify-between text-xs text-[color:var(--text-muted)]">
            <span>{data.length} registros</span>
            <span className="hidden sm:inline">Use a rolagem vertical para navegar pelos dados ↑ ↓</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecebimentosTable;




